/**
 * Login page. Handles initial credentials submission, the
 * NEW_PASSWORD_REQUIRED challenge for first-time logins
 * (builders provisioned via provisionTenant.ts arrive in
 * FORCE_CHANGE_PASSWORD state and must set a permanent
 * password before continuing), and the standard Cognito
 * forgot-password flow (ForgotPassword -> ConfirmForgotPassword)
 * for a signed-out builder who doesn't remember their password.
 *
 * The forgot-password screens are internal mode state, not
 * separate routes — same approach the NEW_PASSWORD_REQUIRED
 * challenge above already uses.
 *
 * round11/password-safeguards — what a real builder's first sign-in taught
 * us (2026-09-15): the password rules were invisible until Cognito rejected
 * the attempt, a pasted temporary password with a stray space just said
 * "Invalid email or password", and "Forgot password?" on an account that
 * still holds a temporary password shows "Enter your code" for a code that
 * Cognito will never send (the pool hides account state, so it answers
 * "sent"). So now: a live rules checklist under every new-password field
 * (PasswordRules), a show-password toggle on every password screen, a
 * whitespace warning on the sign-in password, a plain explanation when a
 * sign-in fails, a one-button path from "password reset required" to the
 * code screen, a "Resend code" button, and a note on the code screen about
 * temporary-password accounts. Client-side checks mirror the pool policy
 * (lib/passwordPolicy.ts); Cognito stays the authority.
 */
import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import type { CognitoUser } from "amazon-cognito-identity-js";
import { useAuth } from "../contexts/auth-context";
import { homePathForUser } from "../lib/authRole";
import { firstPasswordProblem, hasEdgeWhitespace } from "../lib/passwordPolicy";
import { StatusMessage } from "../components/StatusMessage";
import { BrandHeader } from "../components/BrandHeader";
import { PasswordRules } from "../components/PasswordRules";
import styles from "./LoginPage.module.css";

type Mode =
  | "signin"
  | "new_password_required"
  | "forgot_request"
  | "forgot_confirm"
  | "forgot_success";

export function LoginPage() {
  const { signIn, completeNewPassword, forgotPassword, confirmForgotPassword, user } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [pendingCognitoUser, setPendingCognitoUser] = useState<CognitoUser | null>(null);
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmNewPassword, setResetConfirmNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // round11/password-safeguards
  const [showPasswords, setShowPasswords] = useState(false);
  const [signInFailed, setSignInFailed] = useState(false);
  const [resetRequired, setResetRequired] = useState(false);
  const [resendStatus, setResendStatus] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  // If already authenticated, redirect away — to whichever section this
  // role can actually use (P4.6-C). An org admin has no tenantId, so /leads
  // would immediately bounce them back out again via ProtectedRoute; sending
  // them straight to /organization avoids that visible flash.
  if (user) {
    navigate(homePathForUser(user), { replace: true });
    return null;
  }

  const passwordType = showPasswords ? "text" : "password";
  const normalizedEmail = () => email.trim().toLowerCase();

  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSignInFailed(false);
    setResetRequired(false);
    setIsSubmitting(true);
    try {
      const result = await signIn(normalizedEmail(), password);
      if (result.kind === "success") {
        navigate(homePathForUser(result.user), { replace: true });
      } else if (result.kind === "new_password_required") {
        setPendingCognitoUser(result.cognitoUser);
        setMode("new_password_required");
      } else {
        setError(result.message);
        if (result.code === "PasswordResetRequiredException") {
          // Someone (us, on the builder's behalf) started a reset: the old
          // password is dead and a code is in their inbox. Offer the exact
          // next step instead of leaving them to guess.
          setResetRequired(true);
        } else {
          setSignInFailed(true);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmNewPassword) {
      setError("Passwords don't match.");
      return;
    }
    const problem = firstPasswordProblem(newPassword);
    if (problem) {
      setError(problem);
      return;
    }
    if (!pendingCognitoUser) {
      setError("Session expired. Please sign in again.");
      setMode("signin");
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await completeNewPassword(pendingCognitoUser, newPassword);
      if (result.kind === "success") {
        // A brand-new org admin provisioned with a temp password lands here
        // on their very first sign-in, so this path matters as much as the
        // ordinary one above.
        navigate(homePathForUser(result.user), { replace: true });
      } else if (result.kind === "error") {
        setError(result.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToSignIn = () => {
    setError(null);
    setResendStatus(null);
    setResetCode("");
    setResetNewPassword("");
    setResetConfirmNewPassword("");
    setMode("signin");
  };

  const goToForgotRequest = () => {
    setError(null);
    setResendStatus(null);
    setMode("forgot_request");
  };

  // From the "password reset required" sign-in error: the code was already
  // emailed when the reset was started, so go straight to the code screen
  // (Resend code is right there if it never arrived).
  const goToEnterCode = () => {
    setError(null);
    setResendStatus(null);
    setResetRequired(false);
    setMode("forgot_confirm");
  };

  const handleForgotRequest = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      // Cognito's ForgotPassword never reveals whether `email` has an
      // account (preventUserExistenceErrors is enabled on the pool
      // client) — forgotPassword() below returns { kind: "sent" } either
      // way, so this always advances to the confirm screen with the same
      // generic copy. Only a real technical failure (rate limit, bad
      // input) surfaces as an error here.
      const result = await forgotPassword(normalizedEmail());
      if (result.kind === "sent") {
        setMode("forgot_confirm");
      } else {
        setError(result.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setResendStatus(null);
    setIsResending(true);
    try {
      const result = await forgotPassword(normalizedEmail());
      if (result.kind === "sent") {
        setResendStatus("We've sent a new code. Give it a minute, and check your spam folder.");
      } else {
        setError(result.message);
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleForgotConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResendStatus(null);
    if (!resetCode.trim()) {
      setError("Enter the code from your email.");
      return;
    }
    if (resetNewPassword !== resetConfirmNewPassword) {
      setError("Passwords don't match.");
      return;
    }
    const problem = firstPasswordProblem(resetNewPassword);
    if (problem) {
      setError(problem);
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await confirmForgotPassword(
        normalizedEmail(), resetCode.trim(), resetNewPassword,
      );
      if (result.kind === "success") {
        setMode("forgot_success");
      } else {
        setError(result.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const showPasswordsToggle = (
    <label className={styles.toggle}>
      <input
        type="checkbox"
        checked={showPasswords}
        onChange={(e) => setShowPasswords(e.target.checked)}
      />
      <span>Show passwords</span>
    </label>
  );

  return (
    // AUD-017: this page previously had no <main> anywhere — everything sat
    // in a plain <div>, so axe flagged landmark-one-main/region on every
    // mode. Wrapping the whole card is enough since only one mode's form
    // renders at a time.
    <main className={styles.loginContainer}>
      <div className={styles.loginCard}>
        <BrandHeader />

        {mode === "signin" && (
          <form onSubmit={handleSignIn} className={styles.form}>
            <h1>Sign in</h1>
            <label className={styles.field}>
              <span>Email</span>
              <input type="email" required value={email}
                     onChange={e => setEmail(e.target.value)}
                     autoComplete="email"
                     autoFocus />
            </label>
            <label className={styles.field}>
              <span>Password</span>
              <input type={passwordType} required value={password}
                     onChange={e => setPassword(e.target.value)}
                     autoComplete="current-password" />
            </label>
            {hasEdgeWhitespace(password) && (
              <p className={styles.hint} data-testid="whitespace-hint">
                Your password starts or ends with a space. If you pasted it from an
                email, remove the extra space.
              </p>
            )}
            <div className={styles.linkRow}>
              {showPasswordsToggle}
              <button
                type="button"
                className={styles.textLink}
                onClick={goToForgotRequest}
              >
                Forgot password?
              </button>
            </div>
            {error && <StatusMessage variant="error" message={error} className={styles.error} />}
            {resetRequired && (
              <div className={styles.helpBlock} data-testid="reset-required-help">
                <button type="button" className={styles.submitButton} onClick={goToEnterCode}>
                  Enter my reset code
                </button>
                <p className={styles.hint}>
                  Didn&rsquo;t get a code? You can request a new one on the next screen.
                </p>
              </div>
            )}
            {signInFailed && !resetRequired && (
              <p className={styles.hint} data-testid="signin-help">
                Signing in with a temporary password from an email? Copy it exactly
                &mdash; it&rsquo;s case-sensitive &mdash; and it expires after 7 days. If
                yours is older, ask us to send a new one.
              </p>
            )}
            <button type="submit" disabled={isSubmitting} className={styles.submitButton}>
              {isSubmitting ? "Signing in…" : "Sign in"}
            </button>
            {/* round7/signup-ui: the one discovery path into /signup — a
                signup page nobody can find from the sign-in screen isn't
                complete. Small, additive, public-page-only change (not an
                authenticated feature). */}
            <div className={styles.signupRow}>
              <span className={styles.subtle}>New here?</span>
              <Link to="/signup" className={styles.textLink}>Create an account</Link>
            </div>
          </form>
        )}

        {mode === "new_password_required" && (
          <form onSubmit={handleNewPassword} className={styles.form}>
            <h1>Set a new password</h1>
            <p className={styles.subtle}>
              Your temporary password worked. Now choose your own password
              &mdash; you&rsquo;ll use it from here on.
            </p>
            <label className={styles.field}>
              <span>New password</span>
              <input type={passwordType} required value={newPassword}
                     onChange={e => setNewPassword(e.target.value)}
                     autoComplete="new-password"
                     aria-describedby="new-password-rules"
                     autoFocus />
            </label>
            <label className={styles.field}>
              <span>Confirm new password</span>
              <input type={passwordType} required value={confirmNewPassword}
                     onChange={e => setConfirmNewPassword(e.target.value)}
                     autoComplete="new-password" />
            </label>
            <PasswordRules id="new-password-rules" password={newPassword} confirm={confirmNewPassword} />
            {showPasswordsToggle}
            {error && <StatusMessage variant="error" message={error} className={styles.error} />}
            <button type="submit" disabled={isSubmitting} className={styles.submitButton}>
              {isSubmitting ? "Setting password…" : "Set password & continue"}
            </button>
          </form>
        )}

        {mode === "forgot_request" && (
          <form onSubmit={handleForgotRequest} className={styles.form}>
            <h1>Reset your password</h1>
            <p className={styles.subtle}>
              Enter your email and we&rsquo;ll send you a code to reset your password.
            </p>
            <p className={styles.hint}>
              Were you just sent a temporary password? Don&rsquo;t reset &mdash; go back
              and sign in with it instead. A reset code can&rsquo;t be issued until
              you&rsquo;ve set your own password.
            </p>
            <label className={styles.field}>
              <span>Email</span>
              <input type="email" required value={email}
                     onChange={e => setEmail(e.target.value)}
                     autoComplete="email"
                     autoFocus />
            </label>
            {error && <StatusMessage variant="error" message={error} className={styles.error} />}
            <button type="submit" disabled={isSubmitting} className={styles.submitButton}>
              {isSubmitting ? "Sending…" : "Send reset code"}
            </button>
            <div className={styles.linkRow}>
              <button type="button" className={styles.textLink} onClick={goToSignIn}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {mode === "forgot_confirm" && (
          <form onSubmit={handleForgotConfirm} className={styles.form}>
            <h1>Enter your code</h1>
            <p className={styles.subtle}>
              If an account exists for {normalizedEmail()}, we&rsquo;ve sent a
              password reset code to that email. Enter it below along with a new password.
            </p>
            <label className={styles.field}>
              <span>Reset code</span>
              <input type="text" required value={resetCode}
                     onChange={e => setResetCode(e.target.value)}
                     inputMode="numeric"
                     autoComplete="one-time-code"
                     autoFocus />
            </label>
            <label className={styles.field}>
              <span>New password</span>
              <input type={passwordType} required value={resetNewPassword}
                     onChange={e => setResetNewPassword(e.target.value)}
                     autoComplete="new-password"
                     aria-describedby="reset-password-rules" />
            </label>
            <label className={styles.field}>
              <span>Confirm new password</span>
              <input type={passwordType} required value={resetConfirmNewPassword}
                     onChange={e => setResetConfirmNewPassword(e.target.value)}
                     autoComplete="new-password" />
            </label>
            <PasswordRules id="reset-password-rules" password={resetNewPassword} confirm={resetConfirmNewPassword} />
            {showPasswordsToggle}
            {resendStatus && <StatusMessage variant="status" message={resendStatus} className={styles.success} />}
            {error && <StatusMessage variant="error" message={error} className={styles.error} />}
            <button type="submit" disabled={isSubmitting} className={styles.submitButton}>
              {isSubmitting ? "Resetting…" : "Reset password"}
            </button>
            <div className={styles.helpBlock} data-testid="code-help">
              <p className={styles.hint}>
                No code after a few minutes? Check your spam folder, then{" "}
                <button type="button" className={styles.textLink} onClick={handleResendCode} disabled={isResending}>
                  {isResending ? "Sending…" : "Resend code"}
                </button>
                .
              </p>
              <p className={styles.hint}>
                Still using a temporary password we emailed you? A reset code can&rsquo;t be
                sent to that account yet &mdash;{" "}
                <button type="button" className={styles.textLink} onClick={goToSignIn}>
                  go back and sign in
                </button>{" "}
                with the temporary password instead.
              </p>
            </div>
            <div className={styles.linkRow}>
              <button type="button" className={styles.textLink} onClick={goToSignIn}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {mode === "forgot_success" && (
          <div className={styles.form}>
            <h1>Password reset</h1>
            <StatusMessage
              variant="status"
              message="Your password has been reset. You can now sign in with your new password."
              className={styles.success}
            />
            <button type="button" className={styles.submitButton} onClick={goToSignIn}>
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
